import { ReactElement, useEffect, useRef, useState } from 'react'

import { coinPagesList } from 'config/coin-pages-list'
import { PERCENTAGES } from 'config/constants'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { fetchCurrencies, getCurrencies } from 'store/reducers/currencies'
import BreadCrumbs from 'components/ui/bread-crumbs'
import { Questions } from 'components/ui/questions'
import { useHeader } from 'utils/use-header'
import { useCurrency } from 'utils/use-currency'

import { GetStaticPaths, GetStaticProps } from 'next'

import { NotSupportedAlert } from './components/not-supported-coin'
import { WhatIsCoin } from './components/what-is-coin'
import { HowToLend } from './components/how-to-lend'
import { MainBlock } from './components/main-coin-block'

import { coinPageData } from './assets/page-data'
import Instruction from './components/instruction'
import { useTranslation } from 'utils/use-translation'

export default function CoinInfo({
  code,
  network,
  index,
}: {
  code: string
  network: string
  index: number
}): ReactElement {
  const { t, replacePlaceholders, locale } = useTranslation()
  const pageCurrency = coinPagesList[`${code}-${network}`]
  const coinPage = coinPageData[pageCurrency?.pageName || code]

  const dispatch = useAppDispatch()
  const currencies = useAppSelector(getCurrencies)

  const isMounted = useRef(true)

  const currency = useCurrency(
    {
      code: pageCurrency.code,
      network: pageCurrency.network,
    },
    currencies,
  )

  const [currenciesCount, setCurrenciesCount] = useState(0)
  const [isSupported, setIsSupported] = useState(
    !currency || currency?.is_loan_deposit_enabled || currency?.is_loan_receive_enabled,
  )

  useEffect(() => {
    dispatch(fetchCurrencies())
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    setIsSupported(!currency || currency?.is_loan_deposit_enabled || currency?.is_loan_receive_enabled)
  }, [currency])

  useEffect(() => {
    const updateCount = async () => {
      const newCurrenciesCount = currencies.filter((el) => el.is_loan_deposit_enabled || el.is_loan_receive_enabled)
        .length

      if (isMounted.current) {
        setCurrenciesCount(newCurrenciesCount)
      }
    }

    void updateCount()
  }, [currencies])

  const placeholders = {
    name: `${pageCurrency.name} (${pageCurrency.code})`
  }

  const head = useHeader(
    replacePlaceholders(t('title_loans'), placeholders),
    replacePlaceholders(t('desc_loans'), placeholders),
    coinPage.questionsBlock.questions,
    index,
    `${pageCurrency.code.toUpperCase()} Loan | Instant ${pageCurrency.code.toLowerCase()} Loans | APR ${PERCENTAGES.loan
    }%`,
  )

  return (
    <>
      {head}
      {!isSupported && <NotSupportedAlert currency={currency} pageCurrency={pageCurrency} />}
      <MainBlock
        currenciesCount={currenciesCount}
        isSupported={isSupported}
        pageCurrency={pageCurrency}
        description={coinPage.description}
        h1={typeof coinPage.h1 === 'function' && coinPage.h1(t, locale, replacePlaceholders)}
      />
      <HowToLend pageCurrency={pageCurrency} />
      <WhatIsCoin coinPage={coinPage} pageCurrency={pageCurrency} />
      {coinPage.videoId && coinPage.videoTitle && (
        <Instruction title={t(coinPage.videoTitle)} videoId={coinPage.videoId} />
      )}
      <Questions
        image={coinPage.questionsBlock.image}
        coinQuestions={coinPage.questionsBlock.questions}
        title={t('faq')}
      />
      <BreadCrumbs
        pages={[
          {
            name: t('loans'),
            url: '/',
          },
          {
            name: `${pageCurrency.name}`,
            url: '',
          },
        ]}
        isInverted={false}
      />
    </>
  )
}

export const getStaticProps: GetStaticProps<
  {
    code: string
    network: string
    index: number
  },
  {
    coin: string
  }
> = async ({ params }) => {
  let coin = params.coin

  let network = coin
  switch (coin?.toLowerCase()) {
    case 'kishu':
    case 'usdt':
    case 'usdc':
    case 'shib':
    case 'zrx':
    case 'uni':
    case 'ilv':
    case 'sushi':
    case 'link':
    case 'lrc':
    case 'mana':
    case 'ape':
    case 'hot':
    case 'aave':
    case 'ftm':
    case 'mkr':
    case 'floki':
    case 'imx':
    case 'jasmy':
    case 'bone':
    case 'vra':
    case 'qnt':
    case 'reef':
    case 'volt':
    case 'xaut':
    case 'comp':
    case 'raca':
    case 'okb':
    case 'amp':
    case 'id':
    case 'flux':
      network = 'eth'
      break
    case 'usdttrc20':
      coin = 'usdt'
      network = 'trx'
      break
    case 'flokibsc':
      network = 'bsc'
      coin = 'floki'
      break
    case 'bnb':
    case 'brise':
    case 'pit':
    case 'babydoge':
    case 'quack':
    case 'inj':
      network = 'bsc'
      break
    case 'xno':
      network = 'nano'
      break
    case 'atom':
      network = 'atom'
      break
    case 'hbar':
      network = 'hbar'
      break
    case 'flr':
      network = 'flr'
      break
  }

  return {
    props: {
      code: coin,
      network: network,
      index: Object.keys(coinPageData).findIndex((curr) => curr === coin) + 10,
    },
  }
}

export const getStaticPaths: GetStaticPaths = ({ locales }) => {
  const paths: Array<{
    params: { coin: string }
    locale: string;
  }> = [];

  for (const key of Object.keys(coinPagesList)) {
    const [code] = key.split('-')
    if (coinPageData[coinPagesList[key]?.pageName || code]) {
      for (const locale of locales || []) {
        paths.push({
          params: { coin: coinPagesList[key].coinPageUrl },
          locale,
        });
      }
    }
  }

  return {
    paths: paths,
    fallback: false,
  }
}
